import { useEffect, useState } from "react";
import { ChevronUp, ChevronDown, Trash2, Plus, Save, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { PROGRAMS } from "@/data";

const move = (arr, i, d) => {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
};

// Standalone pages (not under /programs/) that can be linked from any menu category
const SPECIAL_PAGES = [
  { to: "/media-press", label: "Media & Press" },
  { to: "/social-gallery", label: "Social Media Gallery" },
];

export default function MenuBuilder() {
  const [menu, setMenu] = useState([]);
  const [openIdx, setOpenIdx] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [status, setStatus] = useState("");

  useEffect(() => {
    api.get("/menu").then((r) => setMenu(r.data || [])).catch(() => {});
    api.get("/pages").then((r) => {
      const opts = {};
      SPECIAL_PAGES.forEach((p) => { opts[p.to] = p.label; });
      Object.values(PROGRAMS).forEach((p) => { opts[`/programs/${p.slug}`] = p.title; });
      r.data.forEach((p) => { opts[`/programs/${p.slug}`] = p.title || p.slug; });
      setRoutes(Object.entries(opts).map(([to, label]) => ({ to, label })));
    }).catch(() => {});
  }, []);

  const upTop = (m) => setMenu(m);
  const setTop = (i, patch) => setMenu((m) => m.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const setItem = (ti, ii, patch) => setMenu((m) => m.map((x, idx) => idx === ti ? { ...x, items: x.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) } : x));

  const save = async () => {
    setStatus("saving");
    try { await api.put("/menu", { value: menu }); setStatus("saved"); setTimeout(() => setStatus(""), 1800); }
    catch { setStatus("error"); }
  };

  const inp = "rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-magenta";

  return (
    <div data-testid="menu-builder" className="max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Menu</h1>
          <p className="text-sm text-slate-500">Arrange top-level menus and their sub-items exactly as they appear on the site.</p>
        </div>
        <div className="flex items-center gap-3">
          {status === "saved" && <span className="text-sm text-green-600">Saved ✓</span>}
          {status === "error" && <span className="text-sm text-red-500">Error</span>}
          <button onClick={save} data-testid="save-menu-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-magenta px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90">
            <Save className="h-4 w-4" /> {status === "saving" ? "Saving…" : "Save Menu"}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {menu.map((m, i) => (
          <div key={i} data-testid={`menu-top-${i}`} className="rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center gap-2 p-3">
              <div className="flex flex-col">
                <button onClick={() => upTop(move(menu, i, -1))} className="text-slate-300 hover:text-brand-ink"><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => upTop(move(menu, i, 1))} className="text-slate-300 hover:text-brand-ink"><ChevronDown className="h-4 w-4" /></button>
              </div>
              <button onClick={() => setOpenIdx(openIdx === i ? null : i)} className="text-slate-400"><ChevronRight className={`h-4 w-4 transition-transform ${openIdx === i ? "rotate-90" : ""}`} /></button>
              <input className={`${inp} flex-1 font-semibold uppercase`} value={m.label} onChange={(e) => setTop(i, { label: e.target.value })} data-testid={`menu-top-label-${i}`} />
              <input className={`${inp} w-28`} value={m.href || ""} onChange={(e) => setTop(i, { href: e.target.value })} placeholder="#anchor" title="Section anchor" />
              <label className="flex items-center gap-1.5 text-xs text-slate-500"><input type="checkbox" checked={!!m.cta} onChange={(e) => setTop(i, { cta: e.target.checked })} /> CTA</label>
              <button onClick={() => setMenu(menu.filter((_, x) => x !== i))} className="rounded p-1.5 text-slate-300 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>

            {openIdx === i && (
              <div className="border-t border-slate-100 bg-slate-50 p-4">
                <input className={`${inp} mb-3 w-full`} value={m.tag || ""} onChange={(e) => setTop(i, { tag: e.target.value })} placeholder="Dropdown tagline" />
                {m.cta && <input className={`${inp} mb-3 w-full`} value={m.shortLabel || ""} onChange={(e) => setTop(i, { shortLabel: e.target.value })} placeholder="Short label (button text)" />}
                <div className="space-y-2">
                  {(m.items || []).map((it, ii) => (
                    <div key={ii} className="flex items-center gap-2">
                      <div className="flex flex-col">
                        <button onClick={() => setTop(i, { items: move(m.items, ii, -1) })} className="text-slate-300 hover:text-brand-ink"><ChevronUp className="h-3.5 w-3.5" /></button>
                        <button onClick={() => setTop(i, { items: move(m.items, ii, 1) })} className="text-slate-300 hover:text-brand-ink"><ChevronDown className="h-3.5 w-3.5" /></button>
                      </div>
                      <input className={`${inp} flex-1`} value={it.label} onChange={(e) => setItem(i, ii, { label: e.target.value })} data-testid={`menu-sub-label-${i}-${ii}`} />
                      <select className={`${inp} w-56`} value={it.to || ""} onChange={(e) => setItem(i, ii, { to: e.target.value })} data-testid={`menu-sub-page-${i}-${ii}`}>
                        <option value="">— No page (section link) —</option>
                        {routes.map((r) => <option key={r.to} value={r.to}>{r.label} ({r.to})</option>)}
                      </select>
                      <button onClick={() => setTop(i, { items: m.items.filter((_, x) => x !== ii) })} className="rounded p-1.5 text-slate-300 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
                <button onClick={() => setTop(i, { items: [...(m.items || []), { label: "New item", to: "" }] })} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium hover:border-brand-magenta hover:text-brand-magenta">
                  <Plus className="h-3.5 w-3.5" /> Add sub-item
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      <button onClick={() => setMenu([...menu, { label: "NEW MENU", href: "#", tag: "", cta: false, items: [] }])} className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-medium hover:border-brand-magenta hover:text-brand-magenta">
        <Plus className="h-4 w-4" /> Add top-level menu
      </button>
    </div>
  );
}
